import { resolveFrDepartment } from "./frDepartment.js";
/**
 * Centroïdes départementaux approximatifs (préfecture du département) — un
 * centrage de carte de repli, jamais une borne de validation (celle-ci reste
 * `isWithinCommuneCoherenceBound`, 25km). Portée (#1022) : métropole (01-95,
 * sans "20" — scindé en 2A/2B) + Corse + DOM/COM 971-976 (Guadeloupe 971,
 * Martinique 972, Guyane 973, Réunion 974, Saint-Pierre-et-Miquelon 975,
 * Mayotte 976). Les collectivités du Pacifique (Polynésie française 987,
 * Nouvelle-Calédonie 988, Wallis-et-Futuna 986) sont hors scope —
 * `departmentCentroidFromZip` y renvoie `null`, le repli final (centre de la
 * France) prenant le relais côté appelant.
 *
 * Saint-Barthélemy et Saint-Martin partagent le zip-préfixe 971 avec la
 * Guadeloupe (aucun préfixe dédié) : leur repli pointe donc sur Basse-Terre,
 * à ~243km (Gustavia) / ~272km (Marigot) — écart notable, à garder en tête
 * pour un futur consommateur (ex. centrage tunnel RDV) qui voudrait raffiner
 * ce cas plutôt que de le découvrir en usage.
 */
export const DEPARTMENT_CENTROIDS = {
    "01": { longitude: 5.2265, latitude: 46.2058 }, // Ain — Bourg-en-Bresse
    "02": { longitude: 3.6199, latitude: 49.5642 }, // Aisne — Laon
    "03": { longitude: 3.3327, latitude: 46.5651 }, // Allier — Moulins
    "04": { longitude: 6.2358, latitude: 44.0925 }, // Alpes-de-Haute-Provence — Digne-les-Bains
    "05": { longitude: 6.0796, latitude: 44.5594 }, // Hautes-Alpes — Gap
    "06": { longitude: 7.262, latitude: 43.7102 }, // Alpes-Maritimes — Nice
    "07": { longitude: 4.5985, latitude: 44.7352 }, // Ardèche — Privas
    "08": { longitude: 4.7196, latitude: 49.7723 }, // Ardennes — Charleville-Mézières
    "09": { longitude: 1.6053, latitude: 42.9648 }, // Ariège — Foix
    "10": { longitude: 4.0744, latitude: 48.2973 }, // Aube — Troyes
    "11": { longitude: 2.3491, latitude: 43.213 }, // Aude — Carcassonne
    "12": { longitude: 2.5758, latitude: 44.3506 }, // Aveyron — Rodez
    "13": { longitude: 5.3698, latitude: 43.2965 }, // Bouches-du-Rhône — Marseille
    "14": { longitude: -0.3707, latitude: 49.1829 }, // Calvados — Caen
    "15": { longitude: 2.4441, latitude: 44.9282 }, // Cantal — Aurillac
    "16": { longitude: 0.156, latitude: 45.6484 }, // Charente — Angoulême
    "17": { longitude: -1.1511, latitude: 46.1603 }, // Charente-Maritime — La Rochelle
    "18": { longitude: 2.3988, latitude: 47.081 }, // Cher — Bourges
    "19": { longitude: 1.7715, latitude: 45.2647 }, // Corrèze — Tulle
    "2A": { longitude: 8.7386, latitude: 41.9192 }, // Corse-du-Sud — Ajaccio
    "2B": { longitude: 9.4508, latitude: 42.6977 }, // Haute-Corse — Bastia
    "21": { longitude: 5.0415, latitude: 47.322 }, // Côte-d'Or — Dijon
    "22": { longitude: -2.7653, latitude: 48.514 }, // Côtes-d'Armor — Saint-Brieuc
    "23": { longitude: 1.871, latitude: 46.1667 }, // Creuse — Guéret
    "24": { longitude: 0.7213, latitude: 45.1848 }, // Dordogne — Périgueux
    "25": { longitude: 6.0243, latitude: 47.238 }, // Doubs — Besançon
    "26": { longitude: 4.8924, latitude: 44.9333 }, // Drôme — Valence
    "27": { longitude: 1.1508, latitude: 49.0242 }, // Eure — Évreux
    "28": { longitude: 1.4894, latitude: 48.4439 }, // Eure-et-Loir — Chartres
    "29": { longitude: -4.1024, latitude: 47.996 }, // Finistère — Quimper
    "30": { longitude: 4.3601, latitude: 43.8367 }, // Gard — Nîmes
    "31": { longitude: 1.4442, latitude: 43.6047 }, // Haute-Garonne — Toulouse
    "32": { longitude: 0.5866, latitude: 43.6459 }, // Gers — Auch
    "33": { longitude: -0.5792, latitude: 44.8378 }, // Gironde — Bordeaux
    "34": { longitude: 3.8767, latitude: 43.6108 }, // Hérault — Montpellier
    "35": { longitude: -1.6778, latitude: 48.1173 }, // Ille-et-Vilaine — Rennes
    "36": { longitude: 1.6956, latitude: 46.8106 }, // Indre — Châteauroux
    "37": { longitude: 0.6848, latitude: 47.3941 }, // Indre-et-Loire — Tours
    "38": { longitude: 5.7245, latitude: 45.1885 }, // Isère — Grenoble
    "39": { longitude: 5.5527, latitude: 46.6739 }, // Jura — Lons-le-Saunier
    "40": { longitude: -0.498, latitude: 43.8903 }, // Landes — Mont-de-Marsan
    "41": { longitude: 1.3359, latitude: 47.5861 }, // Loir-et-Cher — Blois
    "42": { longitude: 4.3872, latitude: 45.4397 }, // Loire — Saint-Étienne
    "43": { longitude: 3.885, latitude: 45.043 }, // Haute-Loire — Le Puy-en-Velay
    "44": { longitude: -1.5536, latitude: 47.2184 }, // Loire-Atlantique — Nantes
    "45": { longitude: 1.9093, latitude: 47.9029 }, // Loiret — Orléans
    "46": { longitude: 1.4414, latitude: 44.4477 }, // Lot — Cahors
    "47": { longitude: 0.6212, latitude: 44.2019 }, // Lot-et-Garonne — Agen
    "48": { longitude: 3.5006, latitude: 44.5183 }, // Lozère — Mende
    "49": { longitude: -0.5632, latitude: 47.4784 }, // Maine-et-Loire — Angers
    "50": { longitude: -1.0899, latitude: 49.1147 }, // Manche — Saint-Lô
    "51": { longitude: 4.3672, latitude: 48.956 }, // Marne — Châlons-en-Champagne
    "52": { longitude: 5.1394, latitude: 48.1113 }, // Haute-Marne — Chaumont
    "53": { longitude: -0.77, latitude: 48.0698 }, // Mayenne — Laval
    "54": { longitude: 6.1844, latitude: 48.6921 }, // Meurthe-et-Moselle — Nancy
    "55": { longitude: 5.1616, latitude: 48.7706 }, // Meuse — Bar-le-Duc
    "56": { longitude: -2.7603, latitude: 47.6582 }, // Morbihan — Vannes
    "57": { longitude: 6.1757, latitude: 49.1193 }, // Moselle — Metz
    "58": { longitude: 3.1601, latitude: 46.9896 }, // Nièvre — Nevers
    "59": { longitude: 3.0573, latitude: 50.6292 }, // Nord — Lille
    "60": { longitude: 2.0808, latitude: 49.4295 }, // Oise — Beauvais
    "61": { longitude: 0.0912, latitude: 48.4322 }, // Orne — Alençon
    "62": { longitude: 2.7815, latitude: 50.2872 }, // Pas-de-Calais — Arras
    "63": { longitude: 3.087, latitude: 45.7772 }, // Puy-de-Dôme — Clermont-Ferrand
    "64": { longitude: -0.3708, latitude: 43.2951 }, // Pyrénées-Atlantiques — Pau
    "65": { longitude: 0.0782, latitude: 43.2328 }, // Hautes-Pyrénées — Tarbes
    "66": { longitude: 2.8948, latitude: 42.6886 }, // Pyrénées-Orientales — Perpignan
    "67": { longitude: 7.7521, latitude: 48.5734 }, // Bas-Rhin — Strasbourg
    "68": { longitude: 7.3585, latitude: 48.0794 }, // Haut-Rhin — Colmar
    "69": { longitude: 4.8357, latitude: 45.764 }, // Rhône — Lyon
    "70": { longitude: 6.1544, latitude: 47.6234 }, // Haute-Saône — Vesoul
    "71": { longitude: 4.828, latitude: 46.3069 }, // Saône-et-Loire — Mâcon
    "72": { longitude: 0.1996, latitude: 48.0061 }, // Sarthe — Le Mans
    "73": { longitude: 5.9178, latitude: 45.5646 }, // Savoie — Chambéry
    "74": { longitude: 6.1294, latitude: 45.8992 }, // Haute-Savoie — Annecy
    "75": { longitude: 2.3522, latitude: 48.8566 }, // Paris
    "76": { longitude: 1.0993, latitude: 49.4431 }, // Seine-Maritime — Rouen
    "77": { longitude: 2.66, latitude: 48.5388 }, // Seine-et-Marne — Melun
    "78": { longitude: 2.1301, latitude: 48.8014 }, // Yvelines — Versailles
    "79": { longitude: -0.4587, latitude: 46.3239 }, // Deux-Sèvres — Niort
    "80": { longitude: 2.2957, latitude: 49.8942 }, // Somme — Amiens
    "81": { longitude: 2.148, latitude: 43.9298 }, // Tarn — Albi
    "82": { longitude: 1.3533, latitude: 44.0181 }, // Tarn-et-Garonne — Montauban
    "83": { longitude: 5.928, latitude: 43.1242 }, // Var — Toulon
    "84": { longitude: 4.8055, latitude: 43.9493 }, // Vaucluse — Avignon
    "85": { longitude: -1.4269, latitude: 46.6705 }, // Vendée — La Roche-sur-Yon
    "86": { longitude: 0.3404, latitude: 46.5802 }, // Vienne — Poitiers
    "87": { longitude: 1.2611, latitude: 45.8336 }, // Haute-Vienne — Limoges
    "88": { longitude: 6.446, latitude: 48.174 }, // Vosges — Épinal
    "89": { longitude: 3.5731, latitude: 47.7982 }, // Yonne — Auxerre
    "90": { longitude: 6.8631, latitude: 47.6383 }, // Territoire de Belfort — Belfort
    "91": { longitude: 2.4404, latitude: 48.6304 }, // Essonne — Évry-Courcouronnes
    "92": { longitude: 2.216, latitude: 48.8924 }, // Hauts-de-Seine — Nanterre
    "93": { longitude: 2.439, latitude: 48.9089 }, // Seine-Saint-Denis — Bobigny
    "94": { longitude: 2.4556, latitude: 48.7904 }, // Val-de-Marne — Créteil
    "95": { longitude: 2.0784, latitude: 49.0367 }, // Val-d'Oise — Cergy
    "971": { longitude: -61.7295, latitude: 15.9958 }, // Guadeloupe — Basse-Terre
    "972": { longitude: -61.0588, latitude: 14.6161 }, // Martinique — Fort-de-France
    "973": { longitude: -52.3135, latitude: 4.9224 }, // Guyane — Cayenne
    "974": { longitude: 55.4504, latitude: -20.8823 }, // Réunion — Saint-Denis
    "975": { longitude: -56.1799, latitude: 46.7825 }, // Saint-Pierre-et-Miquelon — Saint-Pierre
    "976": { longitude: 45.2265, latitude: -12.7823 }, // Mayotte — Mamoudzou
};
/**
 * Résout le centroïde départemental d'un zip, ou `null` — zip invalide,
 * département non dérivable, ou département hors de la portée couverte par
 * `DEPARTMENT_CENTROIDS` (ex. Polynésie française). L'appelant est censé
 * retomber sur son propre repli final (ex. centre de la France).
 */
export function departmentCentroidFromZip(zip) {
    const resolution = resolveFrDepartment(zip);
    return resolution ? (DEPARTMENT_CENTROIDS[resolution.centroidKey] ?? null) : null;
}
//# sourceMappingURL=departmentCentroids.js.map